import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useMsal } from '@azure/msal-react';
import { loginRequest, clearStaleInteraction } from '../auth/msalConfig';
import { clearConversationsForCurrentUser, initChatHistorySync, resetSyncState } from '../utils/chatStore';
import {
  ENTRA_TOKEN_KEY,
  TEAMS_INTERACTIVE_AUTH_KEY,
  LOCAL_USER_KEY,
  LOCAL_ROLE_TOKEN_KEY,
  LEGACY_DEMO_USER_KEY,
  LEGACY_DEMO_TOKEN_KEY,
  LOCAL_SESSION_TOKEN_KEY,
} from '../auth/constants';
import {
  IDLE_TIMEOUT_MS, IDLE_WARNING_MS, TOKEN_REFRESH_SKEW_MS,
  isTokenExpired, markActivity, msSinceActivity,
  notifySessionEnd, onSessionEnd, resetSessionEndSignal,
} from '../auth/session';

const AuthContext = createContext(null);

const ACTIVITY_EVENTS = ['mousedown', 'keydown', 'touchstart', 'wheel'];
const IDLE_TICK_MS = 5000;

/**
 * Automatic logout, shared by both auth modes.
 *
 * - listens for session-end signals (401 from the API, token expiry) and calls `endSession`
 * - runs the idle timer, exposing a countdown so the UI can warn before signing out
 *
 * Activity is user input *or* an API call (see auth/session.js): a deep draft polls for
 * several minutes while the user just watches, and logging them out then would be its own bug.
 */
function useAutoLogout({ enabled, endSession }) {
  const [idleSecondsLeft, setIdleSecondsLeft] = useState(null);
  const endRef = useRef(endSession);
  endRef.current = endSession;

  useEffect(() => onSessionEnd((reason) => endRef.current?.(reason)), []);

  useEffect(() => {
    if (!enabled) { setIdleSecondsLeft(null); return undefined; }

    const onActivity = () => { markActivity(); setIdleSecondsLeft(null); };
    for (const ev of ACTIVITY_EVENTS) window.addEventListener(ev, onActivity, { passive: true });

    const tick = setInterval(() => {
      const idleFor = msSinceActivity();
      if (idleFor >= IDLE_TIMEOUT_MS) {
        setIdleSecondsLeft(null);
        notifySessionEnd('idle');
      } else if (idleFor >= IDLE_TIMEOUT_MS - IDLE_WARNING_MS) {
        setIdleSecondsLeft(Math.ceil((IDLE_TIMEOUT_MS - idleFor) / 1000));
      } else {
        setIdleSecondsLeft((prev) => (prev === null ? prev : null));
      }
    }, IDLE_TICK_MS);

    return () => {
      clearInterval(tick);
      for (const ev of ACTIVITY_EVENTS) window.removeEventListener(ev, onActivity);
    };
  }, [enabled]);

  const stayActive = useCallback(() => { markActivity(); setIdleSecondsLeft(null); }, []);

  return { idleSecondsLeft, stayActive };
}

const ROLES = ['Legal Analyst', 'Reviewer', 'Admin'];

/**
 * Accept a password sign-in.
 *
 * Shared by both auth modes because a deployment can offer Entra and local accounts at the
 * same time - a tenant colleague and an external lawyer signing into the same app.
 */
function signInWithLocalToken({ token, user: u }) {
  localStorage.setItem(LOCAL_SESSION_TOKEN_KEY, token);
  localStorage.setItem(LOCAL_USER_KEY, JSON.stringify({
    id: u.id, name: u.name || u.email, email: u.email, role: u.role, firmId: u.firmId,
  }));
  // Full reload rather than setState: every context (brand, language, budget) reads identity
  // at mount, and a half-updated app after a sign-in is how stale-permission bugs start.
  window.location.assign('/');
}

// Keep in step with ADMIN_EMAILS in server/middleware/entraAuth.js. The server is the
// enforcement point; this list only decides what the UI offers, so a mismatch either shows
// a panel the server will refuse or hides one the user is entitled to.
const ADMIN_EMAILS = [
  'bengisu.sarac@unifytech.com.tr',
  'umit.seyhan@unifytech.com.tr',
];

function isEmbeddedInIframe() {
  try {
    return window.self !== window.top;
  } catch {
    return true;
  }
}

function resolveRole(account) {
  const email = (account?.username || account?.preferred_username || '').toLowerCase();
  if (ADMIN_EMAILS.includes(email)) return 'Admin';
  return 'Reviewer';
}

function resolveRoleFromEmail(email = '') {
  if (ADMIN_EMAILS.includes(email.toLowerCase())) return 'Admin';
  return 'Reviewer';
}

function decodeJwtPayload(token) {
  try {
    const part = token.split('.')[1];
    const json = atob(part.replace(/-/g, '+').replace(/_/g, '/'));
    return JSON.parse(json);
  } catch {
    return {};
  }
}

async function tryTeamsSSO() {
  if (!isEmbeddedInIframe()) return null;
  try {
    const teams = await import('@microsoft/teams-js');
    await teams.app.initialize();
    const token = await teams.authentication.getAuthToken();
    return token || null;
  } catch {
    return null;
  }
}

// ─── Mock auth (server has no Entra – local dev only) ───────────────────────

function migrateLegacyLocalAuth() {
  try {
    const legacyUser = localStorage.getItem(LEGACY_DEMO_USER_KEY);
    if (legacyUser && !localStorage.getItem(LOCAL_USER_KEY)) {
      localStorage.setItem(LOCAL_USER_KEY, legacyUser);
      localStorage.removeItem(LEGACY_DEMO_USER_KEY);
    }
    const legacyTok = localStorage.getItem(LEGACY_DEMO_TOKEN_KEY);
    if (legacyTok && !localStorage.getItem(LOCAL_ROLE_TOKEN_KEY)) {
      localStorage.setItem(LOCAL_ROLE_TOKEN_KEY, legacyTok);
      localStorage.removeItem(LEGACY_DEMO_TOKEN_KEY);
    }
  } catch {}
}

function useMockAuth() {
  migrateLegacyLocalAuth();
  const [user, setUser] = useState(() => {
    const saved = localStorage.getItem(LOCAL_USER_KEY);
    if (saved) try { return JSON.parse(saved); } catch { return null; }
    return { id: 'local-user', role: 'Legal Analyst', name: 'User' };
  });
  const clearLocalSession = useCallback(() => {
    setUser(null);
    localStorage.removeItem(LOCAL_USER_KEY);
    localStorage.removeItem(LOCAL_ROLE_TOKEN_KEY);
    // Without this, signing out left a valid password session in storage and the next page
    // load silently signed the user back in.
    localStorage.removeItem(LOCAL_SESSION_TOKEN_KEY);
    localStorage.removeItem(LEGACY_DEMO_USER_KEY);
    localStorage.removeItem(LEGACY_DEMO_TOKEN_KEY);
  }, []);

  // Local role tokens never expire, but the idle timeout still applies: the same machine
  // should not stay signed into a legal workspace after being left alone.
  const endSession = useCallback((reason = 'idle') => {
    clearLocalSession();
    notifySessionEnd(reason);
  }, [clearLocalSession]);

  const { idleSecondsLeft, stayActive } = useAutoLogout({ enabled: !!user, endSession });

  const login = (role) => {
    resetSessionEndSignal();
    markActivity();
    const u = { id: 'local-user', role: role || 'Legal Analyst', name: 'User' };
    setUser(u);
    localStorage.setItem(LOCAL_USER_KEY, JSON.stringify(u));
    localStorage.setItem(LOCAL_ROLE_TOKEN_KEY, u.role);
  };
  const logout = () => {
    resetSessionEndSignal();
    clearLocalSession();
  };
  return { user, login, logout, roles: ROLES, isEntraEnabled: false, idleSecondsLeft, stayActive, signInWithLocalToken };
}

// ─── Entra / Teams SSO ───────────────────────────────────────────────────────

/**
 * A password session that is already in storage, or null.
 *
 * Needed because BOTH providers must understand password sign-in. AuthProvider picks the
 * Entra provider whenever Entra is configured - which is production - and that provider used
 * to build its user purely from MSAL accounts. So a password sign-in stored its token, did
 * its full reload, and came back to a provider that had never heard of it: user null, route
 * guard fails, straight back to /login. Every password account, every time, while the server
 * had answered 200 with a perfectly good token.
 *
 * An expired token is NOT restored. Resurrecting a dead session just moves the failure to
 * the first API call, which is how the app previously ended up "signed in" with a token the
 * server rejects.
 */
function readLocalSession() {
  try {
    const token = localStorage.getItem(LOCAL_SESSION_TOKEN_KEY);
    const raw = localStorage.getItem(LOCAL_USER_KEY);
    if (!token || !raw || isTokenExpired(token)) return null;
    const u = JSON.parse(raw);
    return u && u.email ? u : null;
  } catch {
    return null;
  }
}

function EntraAuthProviderInner({ children }) {
  const { instance, accounts } = useMsal();
  // Seeded from a stored password session, not null: this provider also serves people who
  // signed in with a password on an Entra-enabled deployment.
  const [user, setUser] = useState(() => readLocalSession());
  const [teamsReady, setTeamsReady] = useState(false);
  // Once a session has ended we must NOT let the effects below rebuild it from the MSAL
  // account cache — that cache outlives the token, and resurrecting the user from it is
  // exactly how the app ended up "signed in" with a token the server rejects.
  const [sessionEnded, setSessionEnded] = useState(false);

  /** Sign out locally and send the user to /login with an explanation. */
  const endSession = useCallback((reason = 'expired') => {
    setSessionEnded(true);
    setUser(null);
    try { localStorage.removeItem(ENTRA_TOKEN_KEY); } catch { /* ignore */ }
    // Both credentials, or the next page load restores the one we did not clear and the
    // user is silently signed back in after logging out.
    try {
      localStorage.removeItem(LOCAL_SESSION_TOKEN_KEY);
      localStorage.removeItem(LOCAL_USER_KEY);
    } catch { /* ignore */ }
    // Sync bookkeeping only - the conversations themselves stay, here and on the server.
    // An idle timeout is the same person coming back, not a request to forget their work.
    try { resetSyncState(); } catch { /* ignore */ }
    notifySessionEnd(reason); // records the reason for the login page; no-op if already signalled
    // Clear MSAL's cached account so the next sign-in is a real one. Local-only: a redirect
    // to Microsoft here would bounce the user mid-session and can loop on expiry.
    try { instance.clearCache?.(); } catch { /* best effort */ }
  }, [instance]);

  const { idleSecondsLeft, stayActive } = useAutoLogout({ enabled: !!user, endSession });

  useEffect(() => {
    if (!isEmbeddedInIframe()) { setTeamsReady(true); return; }

    tryTeamsSSO().then((token) => {
      if (!token) { setTeamsReady(true); return; }

      localStorage.setItem(ENTRA_TOKEN_KEY, token);

      const claims = decodeJwtPayload(token);
      const email = (claims.preferred_username || claims.upn || claims.email || '').toLowerCase();
      const name = claims.name || claims.given_name || email || 'Teams User';
      const id = claims.oid || claims.sub || 'teams-user';

      setUser({ id, name, email, role: resolveRoleFromEmail(email) });
      setTeamsReady(true);
    });
  }, []);

  useEffect(() => {
    if (sessionEnded) return;
    if (!accounts?.length) {
      // No Microsoft account does not mean no session - it may be a password sign-in.
      const local = readLocalSession();
      if (local) { setUser(local); return; }
      if (!localStorage.getItem(ENTRA_TOKEN_KEY)) {
        setUser(null);
      }
      return;
    }
    const account = accounts[0];
    try {
      if (!instance.getActiveAccount()) instance.setActiveAccount(account);
    } catch {}
    // Establish the session from the account immediately so the route guard
    // passes; refreshing the bearer (id) token is best-effort and must not be
    // able to strand a successfully signed-in user on the login page.
    setUser({
      id: account.localAccountId || account.homeAccountId,
      name: account.name || account.username || '',
      role: resolveRole(account),
    });
    instance.acquireTokenSilent({ ...loginRequest, account })
      .then((result) => {
        if (result?.idToken) localStorage.setItem(ENTRA_TOKEN_KEY, result.idToken);
      })
      .catch(() => { /* the watchdog below decides: renewable → refresh, expired → sign out */ });
  }, [instance, accounts, sessionEnded]);

  // Token watchdog. The id token lives ~60–90 min and the server verifies `exp`, so a stale
  // token means every API call 401s. We renew silently before expiry (no popup); if renewal
  // fails and the token is actually expired, the session is over — sign the user out instead
  // of leaving them in a signed-in UI that cannot load anything.
  useEffect(() => {
    if (sessionEnded || !user) return undefined;
    const account = accounts?.[0];
    let cancelled = false;

    const check = async () => {
      if (cancelled) return;

      // A password session is judged on ITS OWN token. Without this the watchdog found no
      // Entra token and no MSAL account and called endSession('expired') on its very first
      // run - which fires immediately - so even a correctly restored password session was
      // killed within a tick of being restored. There is no silent renewal to attempt: a
      // local token has a fixed life and the user signs in again when it ends.
      const localToken = localStorage.getItem(LOCAL_SESSION_TOKEN_KEY);
      if (localToken) {
        if (isTokenExpired(localToken)) endSession('expired');
        return;
      }

      const token = localStorage.getItem(ENTRA_TOKEN_KEY);
      if (token && !isTokenExpired(token, TOKEN_REFRESH_SKEW_MS)) return; // still comfortably valid
      if (!account) {
        // Teams tab SSO: the token came from getAuthToken(), not MSAL, so there is no
        // account to renew from. A dead token means a dead session — end it here rather
        // than waiting for the next API call to 401.
        if (!token || isTokenExpired(token)) endSession('expired');
        return;
      }
      try {
        const result = await instance.acquireTokenSilent({ ...loginRequest, account });
        if (cancelled) return;
        if (result?.idToken) { localStorage.setItem(ENTRA_TOKEN_KEY, result.idToken); return; }
        throw new Error('silent refresh returned no id token');
      } catch {
        if (cancelled) return;
        // Renewal failed. If what we hold is already expired there is nothing to stand on.
        const current = localStorage.getItem(ENTRA_TOKEN_KEY);
        if (!current || isTokenExpired(current)) endSession('expired');
      }
    };

    const onVisible = () => { if (document.visibilityState === 'visible') check(); };
    check();
    const id = setInterval(check, 60 * 1000);
    window.addEventListener('focus', check);   // reactive: user came back to the tab
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      cancelled = true;
      clearInterval(id);
      window.removeEventListener('focus', check);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [instance, accounts, sessionEnded, endSession, user]);

  const login = async () => {
    // A fresh sign-in clears the "session over" state and its recorded reason.
    setSessionEnded(false);
    resetSessionEndSignal();
    markActivity();
    if (isEmbeddedInIframe()) {
      const token = await tryTeamsSSO();
      if (token) {
        localStorage.setItem(ENTRA_TOKEN_KEY, token);
        const claims = decodeJwtPayload(token);
        const email = (claims.preferred_username || claims.upn || claims.email || '').toLowerCase();
        const name = claims.name || claims.given_name || email || 'Teams User';
        setUser({ id: claims.oid || 'teams-user', name, email, role: resolveRoleFromEmail(email) });
        return;
      }
      // MSAL loginPopup is blocked in Teams (popup_window_error). Open a top-level child window
      // and use loginRedirect + notifySuccess. See https://aka.ms/teamsfeauth
      const teams = await import('@microsoft/teams-js');
      await teams.app.initialize();
      const { authentication } = teams;
      const authUrl = `${window.location.origin}/login?bagentTeams=1`;
      try {
        localStorage.setItem(TEAMS_INTERACTIVE_AUTH_KEY, '1');
        const idToken = await new Promise((resolve, reject) => {
          const fail = (reason) => {
            try { localStorage.removeItem(TEAMS_INTERACTIVE_AUTH_KEY); } catch {}
            const msg = reason instanceof Error ? reason.message : String(reason ?? 'Teams auth failed');
            reject(Object.assign(new Error(msg), { errorCode: 'TEAMS_INTERACTIVE_FAILED' }));
          };
          try {
            authentication.authenticate({
              url: authUrl,
              width: 600,
              height: 535,
              successCallback: (t) => resolve(t),
              failureCallback: fail,
            });
          } catch (e) {
            try { localStorage.removeItem(TEAMS_INTERACTIVE_AUTH_KEY); } catch {}
            reject(e);
          }
        });
        if (idToken) {
          localStorage.setItem(ENTRA_TOKEN_KEY, idToken);
          const claims = decodeJwtPayload(idToken);
          const email = (claims.preferred_username || claims.upn || claims.email || '').toLowerCase();
          const name = claims.name || claims.given_name || email || 'Teams User';
          setUser({ id: claims.oid || 'teams-user', name, email, role: resolveRoleFromEmail(email) });
        }
      } catch (e) {
        try { localStorage.removeItem(TEAMS_INTERACTIVE_AUTH_KEY); } catch {}
        throw e;
      }
      return;
    }
    try {
      await instance.loginRedirect(loginRequest);
    } catch (e) {
      // A stale interaction flag from an interrupted redirect blocks every later attempt.
      // Clear it and retry once, so a stuck user recovers by clicking sign-in again rather
      // than having to clear site data.
      if (e?.errorCode === 'interaction_in_progress' || /interaction_in_progress/i.test(e?.message || '')) {
        clearStaleInteraction();
        await instance.loginRedirect(loginRequest);
        return;
      }
      throw e;
    }
  };

  const logout = () => {
    // Explicit sign-out: a full Entra logout is right here (the user asked for it), unlike
    // automatic expiry which stays local.
    setSessionEnded(true);
    // Whether this session came from Microsoft decides how to end it - read BEFORE clearing.
    const wasEntra = !!localStorage.getItem(ENTRA_TOKEN_KEY) || !!accounts?.length;
    localStorage.removeItem(ENTRA_TOKEN_KEY);
    // Clear the password session too, or the next load restores it and signs the user
    // straight back in after they asked to leave.
    // Clear conversations BEFORE the identity, because the store keys them by the
    // signed-in user - remove the user first and it can no longer tell which ones to drop.
    try { clearConversationsForCurrentUser(); } catch { /* ignore */ }
    // Drop what this browser thinks the server already has. Without it the next person to
    // sign in on this machine starts with the previous user's push bookkeeping, and their
    // first save would skip conversations it wrongly believes are already uploaded.
    try { resetSyncState(); } catch { /* ignore */ }
    try {
      localStorage.removeItem(LOCAL_SESSION_TOKEN_KEY);
      localStorage.removeItem(LOCAL_USER_KEY);
    } catch { /* ignore */ }
    setUser(null);
    resetSessionEndSignal();
    const uri = window.location.origin;
    if (!wasEntra) {
      // A password session has nothing to log out of at Microsoft, and sending them there
      // would ask them to end a Microsoft session they never started.
      window.location.assign('/login');
      return;
    }
    if (isEmbeddedInIframe()) {
      instance.logoutPopup({ postLogoutRedirectUri: uri }).catch(console.error);
    } else {
      instance.logoutRedirect({ postLogoutRedirectUri: uri }).catch(console.error);
    }
  };

  // Bring the server's copy of this user's chat history into the browser once they are
  // known. Runs per signed-in identity, not per render: pullFromServer() is idempotent, but
  // re-running it on every render would still cost a request each time.
  useEffect(() => {
    if (!user?.email) return;
    initChatHistorySync();
  }, [user?.email]);

  return (
    <AuthContext.Provider value={{
      user, login, logout, roles: ROLES, isEntraEnabled: true, teamsReady, signInWithLocalToken,
      idleSecondsLeft, stayActive,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

function MockAuthProvider({ children }) {
  const value = useMockAuth();
  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function AuthProvider({ children, entraEnabled }) {
  if (!entraEnabled) {
    return <MockAuthProvider>{children}</MockAuthProvider>;
  }
  return <EntraAuthProviderInner>{children}</EntraAuthProviderInner>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
